import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles } from '@material-ui/core';
import { useToast } from '@redshelf/redshelf-ui';

import { ConfirmationDialog, UsageBar } from 'components';
import {
  ContentAvailableToDownload,
  DownloadedContent,
  OfflineUnavailableMessage,
} from 'components/Offline';
import {
  AnalyticsModifiersContext,
  BookValuesContext,
  DRMModifiersContext,
  DRMValuesContext,
  OfflineModifiersContext,
  OfflineValuesContext,
  NetworkStatusValuesContext,
  UserContentValuesContext,
} from 'contexts';
import { useDimensions, useDownloadToast } from 'hooks';
import { OFFLINE_CHAPTER_STATUS, OFFLINE_EVENT_TYPES } from 'constants';
import {
  getAvailableForDownloadChildToFocus,
  getChildToFocus,
  getOfflineUsageBarProps,
  getOfflineUnavailableMessageProps,
  focusButton,
} from './OfflineMenuHelpers';

const useStyles = makeStyles((theme) => {
  return {
    /* Styles applied to the root element. */
    root: {
      display: 'flex',
      flexDirection: 'column',
      margin: 0,
      overflowY: 'none',
      maxHeight: 'calc(100vh - 170px)',
      [theme.breakpoints.down('xs')]: {
        maxHeight: 'calc(100vh - 60px)', // vh minus title bar height
      },
    },
    usageBar: {
      position: 'sticky',
      borderTop: `1px solid ${theme.palette.grey[300]}`,
    },
    contentContainer: {
      margin: 0,
      overflowY: 'auto',
      maxHeight: ({ usageBarHeight }) => {
        return `calc(100vh - ${usageBarHeight}px)`;
      },
    },
  };
});

const OfflineMenu = ({
  className,
  children,
  backButtonRef,
  setCancelDownloadDialogProps,
  titleRef,
  onClose,
  ...props
}) => {
  /**
   * Get Context Values and Modifiers
   */
  const analytics = useContext(AnalyticsModifiersContext);
  const { setDidAttemptToExceedOfflinePercent } =
    useContext(DRMModifiersContext);

  const drmValuesContext = useContext(DRMValuesContext);
  const {
    offlinePercentMaximum,
    allowedToOfflineAdditionalMarkers,
    didAttemptToExceedOfflinePercent,
  } = drmValuesContext;

  const { addMarker, removeMarker, clear, downloadWholeBook } = useContext(
    OfflineModifiersContext,
  );
  const {
    markersDownloaded,
    markersDownloading,
    markersProgress,
    markersToDownload,
    wholeBookDownloaded,
    wholeBookDownloading,
    offlineMenuProps,
  } = useContext(OfflineValuesContext);

  const {
    markerStatusMap,
    markersForDownloadedContentSection: downloadedContent,
    markersShouldOpen,
  } = offlineMenuProps;

  const { isOffline } = useContext(NetworkStatusValuesContext);

  const { offlineSessions, offlineSession } = useContext(
    UserContentValuesContext,
  );

  const { book } = useContext(BookValuesContext);
  const topLevelMarkers = book.topLevelMarkers;

  /**
   * Hooks
   */
  const { dimensions } = useDimensions(document.getElementById('usage-bar'));

  const classes = useStyles({
    usageBarHeight: dimensions ? dimensions[1] : null,
  });
  const markersDownloadingRef = useRef(new Set());

  const { closeToast, enqueueToast } = useToast();

  useDownloadToast({
    markersDownloaded,
    markersDownloading,
    markersProgress,
    markersToDownload,
    setCancelDownloadDialogProps,
    markersDownloadingRef,
  });

  /**
   * Get Props for Offline UsageBar
   */
  const offlineUsageBarProps = getOfflineUsageBarProps(drmValuesContext);

  /**
   * Get Props for OfflineUnavailableMessage
   */
  const offlineUnavailableMessageProps = getOfflineUnavailableMessageProps(
    offlineSession,
    offlineSessions,
    drmValuesContext,
    topLevelMarkers,
    isOffline,
  );

  /**
   * Props for DownloadedContent and ContentAvailableToDownload Components
   */
  const [isDeleteAllDownloadsConfirmOpen, setIsDeleteAllDownloadsConfirmOpen] =
    useState(false);
  const [deleteOfflineToast, setDeleteOfflineToast] = useState(null);
  const [isDownloadAllClicked, setIsDownloadAllClicked] = useState(false);
  const [itemToScrollTo, setItemToScrollTo] = useState(null);

  const downloadedItemsRef = useRef(null);
  const downloadedContentRef = useRef(null);
  const contentToDownloadItemsRef = useRef(null);
  const usageBarRef = useRef(null);

  const handleRemoveItem = useCallback(
    async (childToFocus, shouldFocusFirstItem, marker, message) => {
      if (childToFocus) {
        focusButton(childToFocus, shouldFocusFirstItem);
      }

      await removeMarker(marker);
      enqueueToast(message, {
        variant: 'success',
      });
    },
    [enqueueToast, removeMarker],
  );

  const availableContent = topLevelMarkers.filter(
    (topLevelMarker) => !downloadedContent?.has(topLevelMarker),
  );

  const statusButtonOnClick = useCallback(
    async (marker, status) => {
      if (status === OFFLINE_CHAPTER_STATUS.DOWNLOADED) {
        analytics.offlineChapterDeleted({
          offlineDeleteType: OFFLINE_EVENT_TYPES.DOWNLOAD.INDIVIDUAL_CHAPTER,
        });

        const message = (
          <span>
            You've successfully deleted{' '}
            <span className="noTranslate">{marker.label}</span>. You will no
            longer have access to this offline.
          </span>
        );

        const isLastParentMarkerDownloaded =
          !markersDownloaded.has(marker.parent) &&
          [...markersDownloaded].every((markerDownloaded) =>
            marker.withDescendents.includes(markerDownloaded),
          );

        if (markersDownloaded.size === 1 || isLastParentMarkerDownloaded) {
          await removeMarker(marker);
          setDeleteOfflineToast(message);
          return;
        }

        const { childToFocus, shouldFocusFirstItem } = getChildToFocus(
          marker,
          downloadedItemsRef,
          markersDownloaded,
        );
        handleRemoveItem(childToFocus, shouldFocusFirstItem, marker, message);
        return;
      }

      if (status === OFFLINE_CHAPTER_STATUS.NOT_DOWNLOADED) {
        analytics.offlineChapterDownloaded({
          offlineDownloadType: OFFLINE_EVENT_TYPES.DELETE.INDIVIDUAL_CHAPTER,
        });
        if (!allowedToOfflineAdditionalMarkers([marker])) {
          setDidAttemptToExceedOfflinePercent(true);
          return;
        }

        markersDownloadingRef.current.add(marker);
        addMarker(marker);
        return;
      }

      if (status === OFFLINE_CHAPTER_STATUS.DOWNLOADING) {
        setCancelDownloadDialogProps({
          isOpen: true,
          marker: marker,
          markersDownloadingRef,
        });
        return;
      }
    },
    [
      addMarker,
      allowedToOfflineAdditionalMarkers,
      analytics,
      handleRemoveItem,
      markersDownloaded,
      removeMarker,
      setCancelDownloadDialogProps,
      setDidAttemptToExceedOfflinePercent,
    ],
  );

  const onDownloadAll = useCallback(() => {
    downloadWholeBook();
    setIsDownloadAllClicked(true);

    analytics.offlineChapterDownloaded({
      offlineDownloadType: OFFLINE_EVENT_TYPES.DOWNLOAD.DOWNLOAD_ALL,
    });
  }, [analytics, downloadWholeBook]);

  const isAnyMarkerDownloaded = Boolean(
    markersDownloaded.size || markersDownloading.size,
  );

  const isLoading =
    (wholeBookDownloading && !wholeBookDownloaded) ||
    (Boolean(book.topLevelMarkers?.length) &&
      Boolean(markersToDownload?.size) &&
      book.topLevelMarkers?.every((t) => markersToDownload?.has(t)));

  const noTOCBookDownload =
    !Boolean(book.topLevelMarkers.length) && // Book is no TOC
    offlinePercentMaximum === 1 && // 100% download available
    !offlineSession?.wholeBook && // not already downloaded
    !isLoading; // not downloading currently

  /**
   * Props for Clear All confirmation Dialog
   */
  const onClearAllConfirm = useCallback(async () => {
    analytics.offlineChapterDeleted({
      offlineDeleteType: OFFLINE_EVENT_TYPES.DELETE.CLEAR_ALL,
    });
    setIsDeleteAllDownloadsConfirmOpen(false);
    await clear();
    await closeToast();
    const message = `You've successfully deleted your download.`;
    setDeleteOfflineToast(message);
  }, [analytics, clear, closeToast, setIsDeleteAllDownloadsConfirmOpen]);

  useEffect(
    function handleLastMarkerDownloaded() {
      if (
        !availableContent.length &&
        (isDownloadAllClicked || markersDownloadingRef.current.size)
      ) {
        backButtonRef.current.focus();
        setIsDownloadAllClicked(false);
        markersDownloadingRef.current = new Set();
      }

      // downloadAll canceled
      if (isDownloadAllClicked && !markersToDownload) {
        setIsDownloadAllClicked(false);
        markersDownloadingRef.current = new Set();
      }
    },
    [availableContent, backButtonRef, isDownloadAllClicked, markersToDownload],
  );

  useEffect(
    function handleDownloadItem() {
      if (
        markersDownloaded.size &&
        markersDownloadingRef.current.size &&
        availableContent.length !== 1
      ) {
        const activeItemTopLevelIndex =
          document.activeElement.closest('li')?.dataset?.topLevelIndex;

        activeItemTopLevelIndex !== undefined &&
          markersDownloadingRef.current.forEach((marker) => {
            const isMarkerDownloadedNow =
              markersDownloaded.has(marker) ||
              [...markersDownloaded].some((markerD) =>
                marker.withDescendents.includes(markerD),
              );
            if (
              isMarkerDownloadedNow &&
              !downloadedContent.has(marker.topLevelMarker) &&
              // find if the focus is on the marker that finished downloading
              +activeItemTopLevelIndex === marker.topLevelMarker.index
            ) {
              const { childToFocus, shouldFocusFirstItem } =
                getAvailableForDownloadChildToFocus(
                  marker,
                  contentToDownloadItemsRef,
                  availableContent,
                  markersDownloaded,
                );

              if (childToFocus) {
                focusButton(childToFocus, shouldFocusFirstItem);
                setItemToScrollTo(childToFocus);
                markersDownloaded.forEach((marker) => {
                  if (markersDownloadingRef.current.has(marker)) {
                    markersDownloadingRef.current.delete(marker);
                  }
                });
              } else {
                backButtonRef?.current.focus();
                markersDownloadingRef.current = new Set();
              }
            }
          });
      }
    },
    [backButtonRef, markersDownloaded, availableContent, downloadedContent],
  );

  useEffect(
    function scrollToItem() {
      const buttonToScrollTo =
        itemToScrollTo &&
        contentToDownloadItemsRef.current?.querySelector(
          `#${itemToScrollTo.id}`,
        );

      if (buttonToScrollTo) {
        const topBorder = titleRef?.current.getBoundingClientRect().bottom;
        const bottomBorder = usageBarRef?.current.getBoundingClientRect().top;

        const isVisible =
          buttonToScrollTo.getBoundingClientRect().top >= topBorder &&
          buttonToScrollTo.getBoundingClientRect().bottom <= bottomBorder;

        if (!isVisible) {
          buttonToScrollTo.scrollIntoView();
        }
        setItemToScrollTo(null);
      }
    },
    [itemToScrollTo, titleRef],
  );

  useEffect(
    function handleLastMarkerDeleted() {
      if (
        deleteOfflineToast &&
        !downloadedContent.size &&
        !downloadedContentRef.current
      ) {
        setDeleteOfflineToast(null);
        backButtonRef?.current.focus();
        enqueueToast(deleteOfflineToast, { variant: 'success' });
      }
    },
    [backButtonRef, deleteOfflineToast, downloadedContent, enqueueToast],
  );

  return (
    <div className={clsx(classes.root, className)} {...props}>
      {offlineUnavailableMessageProps ? (
        // need to create an offline message for cases when offlineEnabled is true but the book has no percentages set in django admin
        <OfflineUnavailableMessage {...offlineUnavailableMessageProps} />
      ) : (
        <>
          <div className={classes.contentContainer}>
            <DownloadedContent
              downloadedContentRef={downloadedContentRef}
              downloadedContent={downloadedContent}
              downloadedItemsRef={downloadedItemsRef}
              isLoading={isLoading}
              markersShouldOpen={markersShouldOpen}
              markerStatusMap={markerStatusMap}
              onClose={onClose}
              setIsDeleteAllDownloadsConfirmOpen={
                setIsDeleteAllDownloadsConfirmOpen
              }
              showWholeBookDownload={offlineSession?.wholeBook}
              statusButtonOnClick={statusButtonOnClick}
            />
            <ContentAvailableToDownload
              availableContent={availableContent}
              contentToDownloadItemsRef={contentToDownloadItemsRef}
              downloadAll={onDownloadAll}
              isAnyMarkerDownloaded={isAnyMarkerDownloaded}
              isLoading={isLoading}
              markerStatusMap={markerStatusMap}
              noTOCBookDownload={noTOCBookDownload}
              onClose={onClose}
              showDownloadAll={offlinePercentMaximum === 1}
              statusButtonOnClick={statusButtonOnClick}
            />
          </div>
          <div ref={usageBarRef} className={classes.usageBar}>
            <UsageBar id="offline-menu-drm" {...offlineUsageBarProps} />
          </div>
        </>
      )}
      {/* Clear All Downloaded Items */}
      <ConfirmationDialog
        title="Are you sure you want to proceed?"
        titleComponent="h4"
        message="Clearing all downloads will mean you will no longer have access to this content offline."
        cancel={{
          label: 'Never Mind',
          action: () => {
            setIsDeleteAllDownloadsConfirmOpen(false);
          },
        }}
        confirm={{
          label: 'Clear Downloads',
          action: onClearAllConfirm,
        }}
        id={'confirmDeleteAllDownloadsDialog'}
        isOpen={isDeleteAllDownloadsConfirmOpen}
        labelledById="delete-all-downloads-confirmation"
      ></ConfirmationDialog>
      {/* Download exceeds allowance */}
      <ConfirmationDialog
        title="This exceeds your download allowance."
        titleComponent="h4"
        message="You cannot download this for offline because it exceeds this publisher's download limit. Please delete previously downloaded content to proceed. "
        cancel={{
          label: 'Close',
          action: () => setDidAttemptToExceedOfflinePercent(false),
        }}
        confirm={{
          label: 'More Information',
          action: () => {
            window.open(
              'https://solve.redshelf.com/hc/en-us/articles/14851332035227-How-to-use-Offline-Access',
              '_blank',
            );
          },
        }}
        id={'confirmExceedsDownloadDialog'}
        isOpen={didAttemptToExceedOfflinePercent}
        labelledById="exceeds-download-allowance"
        swapButtons
      />
    </div>
  );
};

OfflineMenu.propTypes = {
  // ref for the additional options menu back button
  backButtonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  className: PropTypes.string,
  children: PropTypes.node,
  onClose: PropTypes.func,
  setCancelDownloadDialogProps: PropTypes.func,
  titleRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export default memo(OfflineMenu);
